import type {
  SortDirection,
  SortKey,
} from "@/components/filter/components/sort/sort-configuration";
import {
  defaultSort,
  defaultSortDirection,
} from "@/components/filter/components/sort/sort-configuration";
import type { PropsWithChildren } from "react";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useUser } from "../../hooks/useUser";

export interface SortContextType {
  activeSort: SortKey;
  setSort: (sort: SortKey) => void;
  sortDirection: SortDirection;
  setSortDirection: (direction: SortDirection) => void;
}

export const SortContext = createContext<SortContextType>({
  activeSort: defaultSort,
  setSort: () => {},
  sortDirection: "descending",
  setSortDirection: () => {},
});

export function SortContextProvider({ children }: PropsWithChildren<any>) {
  const [activeSort, setActiveSort] = useState<SortKey>(defaultSort);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultSortDirection);

  const { permissions } = useUser();

  const setSort = useCallback((sort: SortKey) => {
    setActiveSort(sort);
  }, []);

  useEffect(() => {
    setActiveSort(defaultSort);
    setSortDirection(defaultSortDirection);
  }, [permissions]);

  const value = useMemo(
    () => ({
      activeSort,
      setSort,
      sortDirection,
      setSortDirection,
    }),
    [activeSort, setSort, sortDirection],
  );

  return <SortContext.Provider value={value}>{children}</SortContext.Provider>;
}

export function useSort() {
  return React.useContext(SortContext);
}
